import type { ProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import { toProjectType } from '@atlassian/jira-common-constants/src/project-types.tsx';
import type FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { extractProjectKey } from '@atlassian/jira-issue-fetch-services-common/src/common/utils/extract-project-key.tsx';
import {
	type UseProjectReturn,
	useProject,
} from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useViewWorkflowData } from '../use-view-workflow-data/main.tsx';

type IssueKey = string;
type IssueTypeId = string;

export type ServiceApi = {
	data: WorkflowServiceData | undefined;
	loading: boolean;
	error: FetchError | undefined;
	fetch: () => Promise<void>;
};

type WorkflowServiceData = {
	hasPermission?: boolean;
	issueTypeId?: IssueTypeId;
	projectId?: string | undefined;
	projectType?: ProjectType | undefined;
	isSimplified?: boolean | undefined;
};

export const emptyResult: ServiceApi = {
	data: undefined,
	loading: false,
	error: undefined,
	fetch: () => Promise.resolve(),
};

const getIssueProjectDataFromContext = (
	useProjectData: UseProjectReturn,
	issueProjectKey: string | null,
): WorkflowServiceData | undefined => {
	if (!useProjectData || !useProjectData.data) return undefined;

	const hasIssueProjectDataInContext = useProjectData.data.key === issueProjectKey;
	if (!hasIssueProjectDataInContext) return undefined;

	return {
		projectId: useProjectData.data.id.toString(),
		projectType: toProjectType(useProjectData.data.type),
		isSimplified: useProjectData.data.isSimplified,
	};
};

export const useProjectViewWorkflowService = (issueKey: IssueKey): ServiceApi => {
	const issueProjectKey = issueKey ? extractProjectKey(issueKey) : null;

	const useProjectData = useProject();
	const projectContextData = getIssueProjectDataFromContext(useProjectData, issueProjectKey);
	const cloudId = useCloudId();
	const issueContextData = useViewWorkflowData({
		projectKey: projectContextData ? undefined : issueProjectKey || undefined,
		issueKey,
		isSimplified: projectContextData && projectContextData.isSimplified,
		cloudId,
	});

	if (!issueKey || !issueProjectKey) {
		return { ...emptyResult };
	}

	const combinedData: WorkflowServiceData | undefined =
		projectContextData || issueContextData.data
			? {
					...projectContextData,
					...issueContextData.data,
				}
			: undefined;

	return { ...issueContextData, data: combinedData };
};
